import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "../../../../hook/use-search-params";
import { DocumentContentService } from "../../../../services/document/document-content.service";
import { useMemo } from "react";
import { documentStatus } from "../../../../constants";

const mapWithPrefix = (items: any[]) => {
  const regex = /\b([A-Za-z0-9]+(?:\.[A-Za-z0-9]+)*[.)])\s*([^<]+)/;

  return items.map((item) => {
    const rawHtml: string = item.Content || "";
    const match = rawHtml.match(regex);

    // Nếu là ảnh hoặc bảng thì không tách prefix
    if (rawHtml.includes("<img") || rawHtml.includes("<table")) {
      return {
        ...item,
        prefix: "",
        content: rawHtml,
      };
    }

    const prefix = match && match[1] ? match[1] : "";
    const content = match && match[2] ? rawHtml.replace(match[0], match[2].trim()) : rawHtml;

    return {
      ...item,
      prefix,
      content,
    };
  });
};

export const useDocumentContent = () => {
  const { searchParams } = useSearchParams();
  const fileId = searchParams.get("fileId");
  const status = searchParams.get("status");

  const { data, isLoading } = useQuery({
    queryKey: ["document-content", fileId],
    queryFn: () => DocumentContentService.getList("", fileId),
    enabled: !!fileId,
  });

  const mappedData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];

    const isPublish = status === documentStatus.publish.value;

    if (!isPublish) {
      const draftItems = data.filter((e: any) => !e.publishVersion);
      return mapWithPrefix(draftItems); // 🟢 mapping prefix
    }

    const publishedItems = data.filter((e: any) => typeof e.publishVersion === "number");
    const maxVersion = Math.max(...publishedItems.map((e: any) => e.publishVersion));

    if (!isFinite(maxVersion)) {
      const draftItems = data.filter((e: any) => !e.publishVersion);
      return mapWithPrefix(draftItems); // 🟢 mapping prefix
    }

    const filteredData = data.filter((e: any) => e.publishVersion === maxVersion);
    return mapWithPrefix(filteredData); // 🟢 mapping prefix
  }, [data, status]);

  const contentTitle = useMemo(() => mappedData.filter((e: { IsContent: boolean }) => !e.IsContent), [mappedData]);

  const position = useMemo(() => {
    const value = Math.max(...mappedData.map((o: any) => o.Position));
    if (value > 0) return value + 1;
    return 1;
  }, [mappedData]);

  const contentSummary = useMemo(
    () => mappedData.filter((e: { ContentSummary: string }) => e.ContentSummary),
    [mappedData],
  );

  return { mappedData, isLoading, contentTitle, position, data, contentSummary };
};
